<script>
import { getCookiePreferenceStatus, saveCookiePreferences } from '@resources/js/api/cookiePreferences';
import { useCookiePreferencesStore } from '@store/useCookiePreferencesStore';

const STORAGE_KEY = 'crowdblue_cookie_preferences';

export default {
  name: 'CpCookiePreferences',
  components: {
    CpButton: require('@resources/js/button').default,
    CpDialogBottom: require('@resources/js/dialog-bottom').default,
    CpBox: require('@resources/js/box').default,
    CpToasted: require('@resources/js/toasted').default,
  },
  data() {
    return {
      PREFERENCE_ACCEPT_ALL: 'accept_all',
      PREFERENCE_REJECT_NON_ESSENTIAL: 'reject_non_essential',
      PREFERENCE_CUSTOMIZE: 'customize',
      showDialog: false,
      loading: false,
      showCustomize: false,
      toasted: [],
      preferenceType: null,
      categories: {
        analytics: false,
        performance: false,
        advertising: false,
      },
    };
  },
  computed: {
    selectedOption: {
      get() {
        return this.preferenceType || this.PREFERENCE_ACCEPT_ALL;
      },
      set(value) {
        this.preferenceType = value;
        if (value === this.PREFERENCE_CUSTOMIZE) {
          this.showCustomize = true;
        } else {
          this.showCustomize = false;
        }
      },
    },
  },
  mounted() {
    this.checkAndOpen();
  },
  methods: {
    handleDialog() {
      this.showDialog = !this.showDialog;
      if (this.showDialog) {
        this.loadStoredPreferencesIntoState();
      } else {
        this.resetState();
      }
    },
    async checkAndOpen() {
      const stored = this.getStoredPreferences();
      if (stored && stored.expires_at && new Date(stored.expires_at) > new Date()) {
        return;
      }
      try {
        const res = await getCookiePreferenceStatus();
        if (res.success && res.data && res.data.should_show_modal) {
          this.showDialog = true;
        }
      } catch (err) {
        if (!this.getStoredPreferences()) {
          this.showDialog = true;
        }
      }
    },
    getStoredPreferences() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    loadStoredPreferencesIntoState() {
      const store = useCookiePreferencesStore();
      store.loadFromStorage();
      this.preferenceType = store.preferenceType;
      if (store.categories) {
        this.categories = {
          analytics: Boolean(store.categories.analytics),
          performance: Boolean(store.categories.performance),
          advertising: Boolean(store.categories.advertising),
        };
      }
      if (this.preferenceType === this.PREFERENCE_CUSTOMIZE) {
        this.showCustomize = true;
      }
    },
    async submit() {
      this.loading = true;
      const effectivePreference = this.preferenceType || this.PREFERENCE_ACCEPT_ALL;
      const payload = {
        preference_type: effectivePreference,
      };
      if (effectivePreference === this.PREFERENCE_CUSTOMIZE) {
        payload.categories = { ...this.categories };
      }
      try {
        const res = await saveCookiePreferences(payload);
        if (res.success && res.data) {
          const store = useCookiePreferencesStore();
          store.setPreferences({
            preference_type: res.data.preference_type,
            categories: res.data.categories,
            expires_at: res.data.expires_at,
          });
          window.dispatchEvent(new CustomEvent('cookie-preferences-updated'));
          this.createAlert({ message: 'Your cookie preferences have been saved.', theme: 'success' });
        } else {
          this.createAlert({
            message: res.message || 'Failed to save cookie preferences. Please try again.',
            theme: 'danger',
          });
        }
      } catch (err) {
        const message = err.response?.data?.message || err.message || 'Failed to save cookie preferences. Please try again.';
        this.createAlert({ message, theme: 'danger' });
      } finally {
        this.loading = false;
        this.showDialog = false;
        this.resetState();
      }
    },
    createAlert({ message = '', theme = 'danger', duration = 3000 }) {
      this.toasted.push({ key: `alert-${new Date().toString()}`, message, theme, duration });
    },
    resetState() {
      this.showCustomize = false;
      this.preferenceType = null;
      this.categories = { analytics: false, performance: false, advertising: false };
    },
  },
};
</script>

<template>
  <span>
    <div class="alerts">
      <CpToasted v-for="toast in toasted" :key="toast.key" :message="toast.message" :theme="toast.theme" :duration="toast.duration" />
    </div>
    <a @click.prevent="handleDialog" href="#" class="cookie-preferences-link">Cookie Preferences</a>
    <CpDialogBottom v-if="showDialog" @dismiss="handleDialog">
      <CpBox>
        <div class="cookiePreferencesContainer">
          <i @click.prevent="handleDialog" class="fal fa-times fa-1-5x black-color close-icon" aria-label="Close"></i>

          <div class="cookiePreferencesData">
            <h2 class="heading2 black-color text-center">Cookie preferences</h2>
            <p class="body black-color text-center cookiePreferencesIntro">
              We use cookies and similar technologies to ensure our website functions properly, understand how it is used, and improve your experience. You can
              choose which categories of cookies you allow. Strictly necessary cookies are always enabled because they are required for website functionality.
            </p>
          </div>

          <div class="cookiePreferencesActions" role="radiogroup" aria-label="Cookie preference">
            <label class="cookiePreferencesRadioOption body">
              <input v-model="selectedOption" type="radio" name="cookie-preference-group" :value="PREFERENCE_ACCEPT_ALL" :disabled="loading" />
              <span>Accept All (Recommended)</span>
            </label>
            <label class="cookiePreferencesRadioOption body">
              <input v-model="selectedOption" type="radio" name="cookie-preference-group" :value="PREFERENCE_REJECT_NON_ESSENTIAL" :disabled="loading" />
              <span>Reject non-Essential Cookies</span>
            </label>
            <label class="cookiePreferencesRadioOption body">
              <input v-model="selectedOption" type="radio" name="cookie-preference-group" :value="PREFERENCE_CUSTOMIZE" :disabled="loading" />
              <span>Customize settings</span>
            </label>
          </div>

          <div v-if="showCustomize" class="cookiePreferencesCustomize">
            <p class="subhead black-color">Choose categories</p>
            <label class="cookiePreferencesCheckbox">
              <input v-model="categories.analytics" type="checkbox" />
              <span>Analytics (usually not necessary for site functionality)</span>
            </label>
            <label class="cookiePreferencesCheckbox">
              <input v-model="categories.performance" type="checkbox" />
              <span>Performance</span>
            </label>
            <label class="cookiePreferencesCheckbox">
              <input v-model="categories.advertising" type="checkbox" />
              <span>Advertising (Not essential)</span>
            </label>
          </div>

          <CpButton class="cookiePreferencesSubmit" block primary :loading="loading" :disabled="loading" @click="submit"> Save preferences </CpButton>
        </div>
      </CpBox>
    </CpDialogBottom>
  </span>
</template>

<style scoped>
.cookie-preferences-link {
  color: unset;
}
.cookiePreferencesContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  position: relative;
  min-width: 320px;
  max-width: 480px;
}
.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
}
.cookiePreferencesData,
.cookiePreferencesActions {
  display: flex;
  flex-direction: column;
}
.cookiePreferencesIntro {
  margin-bottom: 1em;
}
.cookiePreferencesActions {
  gap: 5px;
}
.cookiePreferencesRadioOption {
  display: flex;
  align-items: center;
  cursor: pointer;
}
.cookiePreferencesRadioOption input {
  margin: 0;
  margin-right: 8px;
  flex-shrink: 0;
}
.cookiePreferencesCustomize {
  margin-top: 1em;
  border-top: 1px solid var(--neutral-1, #e0e0e0);
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.cookiePreferencesCheckbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.cookiePreferencesCheckbox input {
  margin: 0;
}
.cookiePreferencesSubmit {
  margin-top: 1em;
}
:deep(.button__content) {
  padding: 0.6em 1em !important;
  margin-right: 0 !important;
}
.alerts {
  position: fixed;
  top: 1em;
  left: 1em;
  right: 1em;
  z-index: 9999;
}
</style>
